/* The ONAM header: ONAM by Saptharushi on the left, MAP's three top-right controls on the right
   (release notes and support, help and FAQs, the account menu), restyled.

   Used on the way in (/home, /entry-hub) and on every ONAM page. Curation keeps MAP's own header.
   MAP's header stays mounted, hidden, underneath (routes.tsx), because it writes session state
   that Curation reads (permissions, organisation). This header only reads. */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/auth-context";
import config from "../../config";
import { LOCKUP } from "../lockup";
import { Glyph } from "../mark";
import { ONAM_PATHS } from "../paths";
import "../onam.css";
import "./chrome.css";

type Pop = "notes" | "help" | "account" | null;
export const FED_EVENT = "onam:federation";
const titleCase = (s: string) => s.replace(/[^a-zA-Z 0-9]+/g, " ").replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

export default function OnamHeader() {
  const auth: any = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [pop, setPop] = useState<Pop>(null);
  const [fedName, setFedName] = useState(localStorage.getItem("federationname") || "");
  const [org, setOrg] = useState<{ name?: string; tag?: string } | null>(null);
  const [notes, setNotes] = useState<{ version?: string; release_date?: string; release_notes_link?: string } | null | undefined>(undefined);
  const [faq, setFaq] = useState<{ question: string; answer: string }[] | null | undefined>(undefined);
  const box = useRef<HTMLDivElement>(null);

  const tenant: any = auth?.appMetadata ? Object.values(auth.appMetadata)[0] : null;
  const orgId: string = tenant?.organization || localStorage.getItem("organizationkey") || "";
  const u = auth?.user || {};
  const name = [u.given_name, u.family_name !== "-" ? u.family_name : ""].filter(Boolean).join(" ") || u.name || "";
  const initials = (name || u.email || "?").split(/\s+/).map((x: string) => x[0]).slice(0, 2).join("").toUpperCase();
  const authRole = localStorage.getItem("authRole") || tenant?.roles?.[0] || "";
  const roles = localStorage.getItem("userRole") || "";
  const isAdmin = [config.superUserRole, config.applicationAdminRole, "organization_admin"].some((r) => r && roles.split(",").includes(r));

  // the current federation changes as people move through the way in and ONAM
  useEffect(() => { setFedName(localStorage.getItem("federationname") || ""); setPop(null); }, [location.pathname, location.search]);
  useEffect(() => {
    const f = () => setFedName(localStorage.getItem("federationname") || "");
    window.addEventListener(FED_EVENT, f); return () => window.removeEventListener(FED_EVENT, f);
  }, []);
  useEffect(() => {
    if (!orgId || !auth?.getorganizationById) return;
    auth.getorganizationById({ args: { organizationID: orgId } }).then((d: any) => setOrg(d || null)).catch(() => setOrg(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  // release notes and FAQs load the first time they are opened
  const open = useCallback((p: Pop) => {
    setPop((cur) => (cur === p ? null : p));
    if (p === "notes" && notes === undefined) auth.getConfig({ args: { organizationId: orgId } }).then((d: any) => setNotes(d?.message || null)).catch(() => setNotes(null));
    if (p === "help" && faq === undefined) auth.getFAQ({ args: {} }).then((d: any) => setFaq(d?.faq || [])).catch(() => setFaq(null));
  }, [auth, orgId, notes, faq]);
  useEffect(() => {
    if (!pop) return;
    const out = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setPop(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setPop(null); };
    document.addEventListener("mousedown", out); window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", out); window.removeEventListener("keydown", esc); };
  }, [pop]);

  // which audience source the page belongs to: DNPA shows its federation, Commerce shows itself,
  // the Advertising page (both sources) shows neither
  const srcMatch = location.pathname.toLowerCase().match(/^\/onam\/advertising\/(dnpa|commerce)(\/|$)/);
  const ctxSrc = srcMatch ? srcMatch[1] : null;
  return (
    <header className="onam-app onam-chrome onam-head">
      <div className="oh-in">
        <a className="oh-brand" href={ONAM_PATHS.home} onClick={(e) => { e.preventDefault(); history.push(ONAM_PATHS.home); }} aria-label="ONAM by Saptharushi, home">
          <img className="lock" src={LOCKUP} alt="ONAM by Saptharushi" />
        </a>
        {ctxSrc === "dnpa" && fedName ? <span className="oh-ctx"><span className="label">Federation</span><b>{fedName}</b></span>
          : ctxSrc === "commerce" ? <span className="oh-ctx"><span className="label">Audience source</span><b>Commerce</b></span> : null}
        <div className="oh-r" ref={box}>
          <button className={`oh-btn ${pop === "notes" ? "on" : ""}`} aria-expanded={pop === "notes"} aria-label="Release notes and support" title="Release notes and support" onClick={() => open("notes")}><Glyph n="notes" /></button>
          <button className={`oh-btn ${pop === "help" ? "on" : ""}`} aria-expanded={pop === "help"} aria-label="Help and FAQs" title="Help and FAQs" onClick={() => open("help")}><Glyph n="help" /></button>
          <button className={`oh-acct ${pop === "account" ? "on" : ""}`} aria-expanded={pop === "account"} title="My account" onClick={() => open("account")}>
            <span className="avatar" aria-hidden="true">{initials}</span><span className="oh-who"><span className="sr-only">My account: </span><b>{name || "My account"}</b><small>{org?.name || ""}</small></span>
          </button>

          {pop === "notes" ? (
            <div className="oh-pop" role="dialog" aria-label="Release notes and support">
              <div className="oh-pop-h"><span className="label">Release notes and support</span></div>
              {notes === undefined ? <p className="oh-wait">Loading…</p> : (
                <dl className="facts left">
                  <dt>Product version</dt><dd>{notes?.version || "Not set"}</dd>
                  <dt>Release date</dt><dd>{notes?.release_date || "Not set"}</dd>
                  <dt>Support</dt><dd><a href="mailto:support@saptharushi.com">support@saptharushi.com</a></dd>
                </dl>)}
              {notes?.release_notes_link ? <a className="btn ghost block" href={notes.release_notes_link} target="_blank" rel="noopener noreferrer"><Glyph n="external" /> Release document</a> : null}
            </div>) : null}

          {pop === "help" ? (
            <div className="oh-pop wide" role="dialog" aria-label="Help and FAQs">
              <div className="oh-pop-h"><span className="label">Help and FAQs</span><a className="link" href="mailto:support@saptharushi.com"><Glyph n="mail" /> Contact support</a></div>
              {faq === undefined ? <p className="oh-wait">Loading…</p> : !faq || !faq.length ? <p className="oh-wait">No FAQs yet. Write to support@saptharushi.com.</p> : (
                <div className="oh-faq">{faq.map((q, i) => <details key={i}><summary>{q.question}<Glyph n="chevron" /></summary><p>{q.answer}</p></details>)}</div>)}
            </div>) : null}

          {pop === "account" ? (
            <div className="oh-pop" role="dialog" aria-label="My account">
              <div className="oh-me"><span className="avatar lg" aria-hidden="true">{initials}</span><span><b>{name || "Signed in"}</b><small>{u.email}</small></span></div>
              <dl className="facts left">
                {org?.name ? <><dt>Organisation</dt><dd>{org.name}</dd></> : null}
                {org?.tag ? <><dt>Organisation role</dt><dd>{org.tag}</dd></> : null}
                {authRole ? <><dt>User role</dt><dd>{titleCase(authRole)}</dd></> : null}
                {fedName ? <><dt>Federation</dt><dd>{fedName}</dd></> : null}
              </dl>
              {isAdmin && config.adminAppUrl ? <a className="link" href={config.adminAppUrl} target="_blank" rel="noopener noreferrer"><Glyph n="external" /> Go to the Admin app</a> : null}
              <button className="btn ghost block" onClick={() => { setPop(null); auth.clearAuth(); }}><Glyph n="logout" /> Log out</button>
            </div>) : null}
        </div>
      </div>
    </header>
  );
}
